/**
 * Cache Warming Service
 * Manages S3 thumbnail cache with localStorage persistence
 */

import { API_BASE_URL } from '../API/api';

class CacheWarmingService {
    constructor() {
        this.isWarming = false;
        this.warmStartTime = null;
        this.warmCompleteTime = null;
        this.cacheStatus = null;
        this.statusCheckInterval = null;
        this.maxStatusChecks = 60; // Check for 60 seconds max
        this.statusCheckCount = 0;
        
        // Cache configuration
        this.CACHE_KEY = 's3_thumbnail_cache';
        this.CACHE_VERSION_KEY = 's3_thumbnail_cache_version';
        this.CACHE_TTL_KEY = 's3_thumbnail_cache_ttl';
        // Bump when thumbnail semantics change (e.g. API verifies S3; old caches had bogus URLs)
        this.CACHE_VERSION = '2.0.0';
        this.CACHE_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds
        
        // Initialize cache from localStorage
        this.initializeCache();
    }

    /**
     * Initialize cache from localStorage
     */
    initializeCache() {
        try {
            const cachedData = localStorage.getItem(this.CACHE_KEY);
            const cacheVersion = localStorage.getItem(this.CACHE_VERSION_KEY);
            const cacheTimestamp = localStorage.getItem(this.CACHE_TTL_KEY);
            
            if (cachedData && cacheVersion === this.CACHE_VERSION && cacheTimestamp) {
                const cacheAge = Date.now() - parseInt(cacheTimestamp);
                if (cacheAge < this.CACHE_TTL) {
                    const cache = JSON.parse(cachedData);
                    console.log(`📦 Loaded ${Object.keys(cache).length} thumbnails from localStorage cache (age: ${Math.round(cacheAge / 1000)}s)`);
                    this.cacheStatus = {
                        status: 'ready',
                        cache_size: Object.keys(cache).length,
                        is_fresh: true,
                        cache_age_seconds: cacheAge / 1000
                    };
                    return true; // Cache is valid
                } else {
                    console.log('📦 localStorage cache expired, clearing...');
                    this.clearLocalCache();
                }
            }
        } catch (error) {
            console.warn('⚠️ Error loading cache from localStorage:', error);
            this.clearLocalCache();
        }
        
        this.cacheStatus = {
            status: 'empty',
            cache_size: 0,
            is_fresh: false,
            cache_age_seconds: null
        };
        return false; // Cache is not valid
    }

    /**
     * Save cache to localStorage
     */
    saveCacheToLocalStorage(cacheData) {
        try {
            localStorage.setItem(this.CACHE_KEY, JSON.stringify(cacheData));
            localStorage.setItem(this.CACHE_VERSION_KEY, this.CACHE_VERSION);
            localStorage.setItem(this.CACHE_TTL_KEY, Date.now().toString());
            console.log(`💾 Saved ${Object.keys(cacheData).length} thumbnails to localStorage`);
        } catch (error) {
            console.error('❌ Error saving cache to localStorage:', error);
        }
    }

    /**
     * Clear localStorage cache
     */
    clearLocalCache() {
        try {
            localStorage.removeItem(this.CACHE_KEY);
            localStorage.removeItem(this.CACHE_VERSION_KEY);
            localStorage.removeItem(this.CACHE_TTL_KEY);
            console.log('🗑️ Cleared localStorage cache');
        } catch (error) {
            console.error('❌ Error clearing localStorage cache:', error);
        }
    }

    /**
     * Get cached thumbnails from localStorage
     */
    getCachedThumbnails() {
        try {
            const cachedData = localStorage.getItem(this.CACHE_KEY);
            if (cachedData) {
                return JSON.parse(cachedData);
            }
        } catch (error) {
            console.warn('⚠️ Error reading cache from localStorage:', error);
        }
        return {};
    }

    /**
     * Start cache warming immediately when DOM loads
     */
    startCacheWarming() {
        console.log('🔥 Starting S3 thumbnail cache warming...');
        
        // Check if we already have valid cache
        if (this.initializeCache() && this.cacheStatus.status === 'ready') {
            console.log('✅ Valid cache found in localStorage, no warming needed');
            return;
        }
        
        if (this.isWarming) {
            console.log('🔥 Cache warming already in progress');
            return;
        }

        this.isWarming = true;
        this.warmStartTime = Date.now();
        this.statusCheckCount = 0;

        // Start the background cache warming
        this.initiateCacheWarm();
        
        // Start monitoring cache status
        this.startStatusMonitoring();
    }

    /**
     * Initiate cache warming by fetching S3 thumbnails from backend
     * Now only requests thumbnails for user's actual projects
     */
    async initiateCacheWarm() {
        try {
            console.log('🔥 Starting intelligent cache warming...');
            
            // First, get the user's projects to know which thumbnails to request
            const userEmail = sessionStorage.getItem('userEmail');
            if (!userEmail) {
                console.warn('⚠️ No user email found, falling back to all projects');
                await this.fetchAllThumbnails();
                return;
            }
            
            console.log('👤 Getting user projects for:', userEmail);
            
            // Get user's projects
            const projectsResponse = await fetch(`${API_BASE_URL}/api/user-projects?email=${encodeURIComponent(userEmail)}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!projectsResponse.ok) {
                throw new Error(`Failed to get user projects: HTTP ${projectsResponse.status}`);
            }

            const projectsData = await projectsResponse.json();
            const userProjects = projectsData.projects || [];
            
            if (userProjects.length === 0) {
                console.log('📭 No projects found for user, skipping thumbnail cache warming');
                this.cacheStatus = {
                    status: 'ready',
                    cache_size: 0,
                    is_fresh: true,
                    cache_age_seconds: 0,
                    method: 'no_projects'
                };
                this.isWarming = false;
                return;
            }
            
            // Extract project IDs
            const projectIds = userProjects.map(project => project.Id || project.id);
            console.log(`🎯 Requesting thumbnails for ${projectIds.length} user projects:`, projectIds);
            
            // Request thumbnails only for user's projects
            const thumbnailsResponse = await fetch(`${API_BASE_URL}/api/s3-thumbnails?project_ids=${projectIds.join(',')}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!thumbnailsResponse.ok) {
                throw new Error(`HTTP ${thumbnailsResponse.status}: ${thumbnailsResponse.statusText}`);
            }

            const data = await thumbnailsResponse.json();
            console.log(`🔥 Fetched ${data.total_thumbnails} S3 thumbnails for user's projects`);
            
            // Store thumbnails in localStorage
            if (data.thumbnails && Object.keys(data.thumbnails).length > 0) {
                localStorage.setItem(this.CACHE_KEY, JSON.stringify(data.thumbnails));
                localStorage.setItem(this.CACHE_VERSION_KEY, this.CACHE_VERSION);
                localStorage.setItem(this.CACHE_TTL_KEY, Date.now().toString());
                
                console.log(`📦 Stored ${Object.keys(data.thumbnails).length} thumbnails in localStorage`);
            }
            
            // Update cache status to ready
            this.cacheStatus = {
                status: 'ready',
                cache_size: data.total_thumbnails || 0,
                is_fresh: true,
                cache_age_seconds: 0,
                method: 'user_projects_only'
            };
            
            this.isWarming = false;
            this.warmCompleteTime = Date.now();
            
            console.log('✅ S3 thumbnail cache warming completed successfully');
            
        } catch (error) {
            console.error('❌ Error fetching S3 thumbnails:', error);
            this.isWarming = false;
        }
    }
    
    /**
     * Fallback method to fetch all thumbnails (used when no user email)
     */
    async fetchAllThumbnails() {
        try {
            console.log('🔥 Fetching ALL S3 thumbnails from backend (fallback)...');
            
            const response = await fetch(`${API_BASE_URL}/api/s3-thumbnails`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();
            console.log(`🔥 Fetched ${data.total_thumbnails} S3 thumbnails from backend`);
            
            // Store thumbnails in localStorage
            if (data.thumbnails && Object.keys(data.thumbnails).length > 0) {
                localStorage.setItem(this.CACHE_KEY, JSON.stringify(data.thumbnails));
                localStorage.setItem(this.CACHE_VERSION_KEY, this.CACHE_VERSION);
                localStorage.setItem(this.CACHE_TTL_KEY, Date.now().toString());
                
                console.log(`📦 Stored ${Object.keys(data.thumbnails).length} thumbnails in localStorage`);
            }
            
            // Update cache status to ready
            this.cacheStatus = {
                status: 'ready',
                cache_size: data.total_thumbnails || 0,
                is_fresh: true,
                cache_age_seconds: 0,
                method: 'all_projects_fallback'
            };
            
            this.isWarming = false;
            this.warmCompleteTime = Date.now();
            
            console.log('✅ S3 thumbnail cache warming completed successfully (fallback)');
            
        } catch (error) {
            console.error('❌ Error fetching all S3 thumbnails:', error);
            this.isWarming = false;
        }
    }

    /**
     * Monitor cache status until it's ready
     * NOTE: Redis removed - cache is always ready with direct URLs!
     */
    startStatusMonitoring() {
        console.log('⚡ Direct URLs - cache is always ready!');
        
        // No monitoring needed - direct URLs are instant
        this.cacheStatus = {
            status: 'ready',
            cache_size: 0,
            cache_last_updated: null,
            cache_age_seconds: null,
            cache_ttl_seconds: 0,
            is_fresh: true,
            is_warming: false,
            method: 'direct_urls'
        };
        
        console.log('✅ Direct URL system ready!');
        this.isWarming = false;
    }

    /**
     * Check current cache status
     * NOTE: Redis removed - using direct URLs now!
     */
    async checkCacheStatus() {
        try {
            // No backend status check needed anymore
            const data = {
                status: "ready",
                cache_size: 0,
                cache_last_updated: null,
                cache_age_seconds: null,
                cache_ttl_seconds: 0,
                is_fresh: true,
                is_warming: false,
                method: "direct_urls"
            };
            
            this.cacheStatus = data;

            const elapsed = Date.now() - this.warmStartTime;
            console.log(`📊 Cache status (${elapsed}ms):`, {
                status: data.status,
                cacheSize: data.cache_size,
                isFresh: data.is_fresh,
                age: data.cache_age_seconds
            });

            // If cache is ready, save to localStorage and mark warming as complete
            if (data.status === 'ready' && data.cache_size > 0) {
                // Fetch the actual cache data and save to localStorage
                await this.fetchAndSaveCacheToLocalStorage();
                
                this.warmCompleteTime = Date.now();
                const totalTime = this.warmCompleteTime - this.warmStartTime;
                console.log(`✅ Cache warming completed in ${totalTime}ms with ${data.cache_size} thumbnails`);
                this.isWarming = false;
            }

        } catch (error) {
            console.error('❌ Error checking cache status:', error);
        }
    }

    /**
     * Save cache data to localStorage by fetching from backend
     */
    async fetchAndSaveCacheToLocalStorage() {
        try {
            const response = await fetch(`${API_BASE_URL}/api/s3-thumbnails`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();
            if (data.thumbnails) {
                this.saveCacheToLocalStorage(data.thumbnails);
                console.log(`💾 Saved ${Object.keys(data.thumbnails).length} thumbnails to localStorage`);
            }
        } catch (error) {
            console.error('❌ Error fetching cache data for localStorage:', error);
        }
    }

    /**
     * Stop status monitoring
     */
    stopStatusMonitoring() {
        if (this.statusCheckInterval) {
            clearInterval(this.statusCheckInterval);
            this.statusCheckInterval = null;
        }
        
        if (this.isWarming) {
            const elapsed = Date.now() - this.warmStartTime;
            console.log(`⏰ Cache warming monitoring stopped after ${elapsed}ms`);
        }
    }

    /**
     * Get current warming status
     */
    getWarmingStatus() {
        return {
            isWarming: this.isWarming,
            warmStartTime: this.warmStartTime,
            warmCompleteTime: this.warmCompleteTime,
            cacheStatus: this.cacheStatus,
            elapsedTime: this.warmStartTime ? Date.now() - this.warmStartTime : 0
        };
    }

    /**
     * Check if cache is ready
     */
    isCacheReady() {
        return this.cacheStatus && 
               this.cacheStatus.status === 'ready' && 
               this.cacheStatus.cache_size > 0;
    }

    /**
     * Get thumbnails for specific project IDs from localStorage cache
     */
    getThumbnailsForProjects(projectIds) {
        const cachedThumbnails = this.getCachedThumbnails();
        const filteredThumbnails = {};
        
        if (projectIds && Array.isArray(projectIds)) {
            projectIds.forEach(projectId => {
                const key = String(projectId);
                const url = cachedThumbnails[key] ?? cachedThumbnails[projectId];
                if (url) {
                    filteredThumbnails[projectId] = url;
                }
            });
        }
        
        console.log(`📦 Retrieved ${Object.keys(filteredThumbnails).length} thumbnails from localStorage for ${projectIds?.length || 0} projects`);
        return filteredThumbnails;
    }

    /**
     * Get all cached thumbnails
     */
    getAllCachedThumbnails() {
        return this.getCachedThumbnails();
    }

    /**
     * Force refresh cache (clear localStorage and start warming)
     */
    forceRefreshCache() {
        console.log('🔄 Force refreshing cache...');
        this.clearLocalCache();
        this.cacheStatus = {
            status: 'empty',
            cache_size: 0,
            is_fresh: false,
            cache_age_seconds: null
        };
        this.startCacheWarming();
    }
}

// Create singleton instance
const cacheWarmingService = new CacheWarmingService();

// Auto-start cache warming when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        cacheWarmingService.startCacheWarming();
    });
} else {
    // DOM is already loaded
    cacheWarmingService.startCacheWarming();
}

// Expose cache service to global scope for debugging
if (typeof window !== 'undefined') {
    window.cacheWarmingService = cacheWarmingService;
    console.log('🔧 Cache warming service available as window.cacheWarmingService');
    console.log('🔧 Available methods: getCacheStats(), forceRefreshCache(), isCacheReady()');
}

export default cacheWarmingService;
